import {
  CalendarIcon,
  MoreVerticalIcon,
  PencilIcon,
  RotateCcwIcon,
  Trash2Icon,
} from 'lucide-react'
import { useState } from 'react'
import { Boton } from '@/components/ui/Boton'
import { Selector } from '@/components/ui/Campo'
import { ChipEstado } from '@/components/ui/ChipEstado'
import {
  Dialogo,
  DialogoContenido,
  DialogoCuerpo,
  DialogoDescripcion,
  DialogoEncabezado,
  DialogoTitulo,
} from '@/components/ui/Dialogo'
import {
  MenuContenido,
  MenuDesplegable,
  MenuDisparador,
  MenuItem,
} from '@/components/ui/MenuDesplegable'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { Confirmacion } from '@/features/admin/Confirmacion'
import { mensajeDeError } from '@/features/admin/ModalMarco'
import { formatearFechaLarga } from '@/features/grilla/formato'
import { cn } from '@/lib/cn'
import { useEsEscritorio } from '@/lib/useEsEscritorio'
import {
  DESCARTAR_COMENTARIO,
  SIN_COMENTARIO,
  TERMINASTE,
  franjaDelPanel,
  type PanelPublicacion as Panel,
} from '@/lib/schemas/revision'
import { ETIQUETA_RED, type RedSocial } from '@/lib/schemas/publicaciones'
import { useComentar, useDecidir, useMarcarResuelto, usePanel } from './apiRevision'
import { HiloComentarios } from './HiloComentarios'
import { VistaPieza } from './VistaPieza'

interface PanelPublicacionProps {
  publicacionId: string
  esRastro: boolean
  alCerrar: () => void
  /** RASTRO: abre C5 en edición con el bloque de nueva versión. */
  alEditar?: (p: Panel) => void
  alReprogramar?: (p: Panel) => void
  /** RASTRO, pieza ya enviada: título, fecha, pilares y redes, sin versión nueva. */
  alEditarDatos?: (p: Panel) => void
  alDarDeBaja?: (p: Panel) => void
  /**
   * Una pieza dada de baja sólo se restaura. Desde la revisión OO del
   * 21/09/2026 la tarjeta de la grilla no tiene menú: todo pasa por acá.
   */
  alRestaurar?: (p: Panel) => void
  /** Modo revisión secuencial: pasa a la siguiente pieza pendiente (§8.2). */
  secuencial?: boolean
  alIrA?: (id: string) => void
}

/**
 * C3 · panel de publicación (§8.4). Modal grande sobre la grilla; pantalla
 * completa en celular. Izquierda, el marco de Instagram; derecha, los datos,
 * la franja del §7.5, el copy y el hilo de comentarios. El cliente decide
 * una vez: aprobar o pedir cambios, con diez segundos para deshacer.
 */
export function PanelPublicacion(props: PanelPublicacionProps) {
  // Al pasar a otra pieza se monta de cero: vuelve a la versión actual y el
  // comentario a medio escribir no se arrastra.
  return <Marco key={props.publicacionId} {...props} />
}

function Marco(props: PanelPublicacionProps) {
  const [version, setVersion] = useState<number | null>(null)
  const [borrador, setBorrador] = useState('')
  const [confirmandoSalida, setConfirmandoSalida] = useState(false)
  const panel = usePanel(props.publicacionId, version)

  // §8.4 — cualquier cierre (la X, Escape, tocar afuera) pasa por la guarda.
  const intentarCerrar = () => {
    if (borrador.trim()) setConfirmandoSalida(true)
    else props.alCerrar()
  }

  return (
    <>
      <Dialogo
        open
        onOpenChange={(abierto) => {
          if (!abierto) intentarCerrar()
        }}
      >
        <DialogoContenido tamano="grande" aria-busy={panel.isPending}>
          {panel.data ? (
            <Contenido
              {...props}
              panel={panel.data}
              alCambiarVersion={setVersion}
              borrador={borrador}
              alEscribir={setBorrador}
            />
          ) : (
            <>
              <DialogoEncabezado>
                <DialogoTitulo className="text-xl font-semibold">
                  Publicación
                </DialogoTitulo>
                <DialogoDescripcion className="sr-only">Cargando</DialogoDescripcion>
              </DialogoEncabezado>
              <DialogoCuerpo>
                {panel.isError ? (
                  <div role="alert" className="flex flex-col items-start gap-3">
                    <p className="text-sm text-tinta-suave">
                      No pudimos abrir la publicación.
                    </p>
                    <Boton
                      variante="secundario"
                      tamano="chico"
                      onClick={() => {
                        void panel.refetch()
                      }}
                    >
                      Reintentar
                    </Boton>
                  </div>
                ) : (
                  <p className="text-sm text-tinta-suave">Cargando…</p>
                )}
              </DialogoCuerpo>
            </>
          )}
        </DialogoContenido>
      </Dialogo>

      <Confirmacion
        abierto={confirmandoSalida}
        alCerrar={() => {
          setConfirmandoSalida(false)
        }}
        titulo="Comentario sin enviar"
        texto={DESCARTAR_COMENTARIO}
        confirmar="Descartar"
        peligro
        alConfirmar={() => {
          setBorrador('')
          props.alCerrar()
          return Promise.resolve()
        }}
      />
    </>
  )
}

function Contenido({
  panel,
  esRastro,
  alCerrar,
  alEditar,
  alReprogramar,
  alEditarDatos,
  alDarDeBaja,
  alRestaurar,
  secuencial,
  alIrA,
  alCambiarVersion,
  borrador,
  alEscribir,
}: PanelPublicacionProps & {
  panel: Panel
  alCambiarVersion: (n: number | null) => void
  borrador: string
  alEscribir: (texto: string) => void
}) {
  const avisar = useAvisar()
  // Una sola barra de decisión: abajo en celular y tableta, al pie de la
  // columna de comentarios en escritorio.
  const enEscritorio = useEsEscritorio()
  const comentar = useComentar(panel.id)
  const decidir = useDecidir(panel.id)
  const resolver = useMarcarResuelto()
  const [lamina, setLamina] = useState(1)
  const [error, setError] = useState<string | null>(null)

  const mirandoAnterior = panel.versionNumero !== panel.versionActual
  const franja = franjaDelPanel(panel.estado, esRastro)
  const puedeDecidir = !esRastro && panel.estado === 'para_revisar' && !mirandoAnterior

  const enviarComentario = (laminaNro: number | null) => {
    setError(null)
    comentar
      .mutateAsync({ texto: borrador, laminaNro })
      .then(() => {
        alEscribir('')
      })
      .catch((e: unknown) => {
        setError(mensajeDeError(e))
      })
  }

  const decidirY = (accion: 'aprobar' | 'cambios') => {
    setError(null)
    const m = accion === 'aprobar' ? decidir.aprobar : decidir.pedirCambios
    m.mutateAsync()
      .then(() => {
        avisar(accion === 'aprobar' ? 'Aprobada' : 'Pedido de cambios enviado a RASTRO', {
          accion: {
            etiqueta: 'Deshacer',
            alHacer: () => {
              void decidir.deshacer.mutateAsync()
            },
          },
        })
        if (secuencial && panel.siguienteParaRevisar && alIrA) {
          alIrA(panel.siguienteParaRevisar)
        } else if (!panel.siguienteParaRevisar) {
          avisar(TERMINASTE)
          alCerrar()
        }
      })
      .catch((e: unknown) => {
        setError(mensajeDeError(e))
      })
  }

  /**
   * Aprobar y Pedir cambios (cliente), o Siguiente para revisar. En celular y
   * tableta es la barra fija de abajo; en escritorio va al pie de la columna
   * de los comentarios, para que la pieza use todo el alto (pedido del
   * 23/09/2026: el posteo entero en la pantalla, sin desplazar).
   */
  const decision = (clase: string) =>
    puedeDecidir ? (
      <div
        className={cn(
          'flex shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end',
          clase,
        )}
      >
        {!panel.comentoEnEstaRonda && (
          <p className="text-xs text-tinta-suave sm:mr-auto">{SIN_COMENTARIO}</p>
        )}
        <Boton
          variante="secundario"
          className="min-h-12 w-full sm:min-h-11 sm:w-auto"
          disabled={!panel.comentoEnEstaRonda || decidir.pedirCambios.isPending}
          onClick={() => {
            decidirY('cambios')
          }}
        >
          Pedir cambios
        </Boton>
        <Boton
          className="min-h-12 w-full sm:min-h-11 sm:w-auto"
          disabled={decidir.aprobar.isPending}
          onClick={() => {
            decidirY('aprobar')
          }}
        >
          Aprobar
        </Boton>
      </div>
    ) : !esRastro && panel.siguienteParaRevisar ? (
      <div className={cn('flex shrink-0 justify-end', clase)}>
        <Boton
          variante="secundario"
          onClick={() => {
            if (panel.siguienteParaRevisar) alIrA?.(panel.siguienteParaRevisar)
          }}
        >
          Siguiente para revisar
        </Boton>
      </div>
    ) : null

  return (
    <>
      <DialogoEncabezado className="flex-row items-start justify-between gap-3">
        <div className="min-w-0">
          <DialogoTitulo className="truncate text-xl font-semibold tracking-tight">
            {panel.titulo}
          </DialogoTitulo>
          <DialogoDescripcion className="text-sm text-tinta-suave">
            {panel.cliente.nombre} ·{' '}
            <span data-momento-panel>
              {formatearFechaLarga(panel.fechaPautada, panel.horaPautada)}
            </span>
            {panel.destino === 'historia' && ' · Historia'}
          </DialogoDescripcion>
        </div>
        {esRastro && panel.dadaDeBaja && (
          <Boton
            variante="secundario"
            tamano="chico"
            className="shrink-0"
            onClick={() => {
              alRestaurar?.(panel)
            }}
          >
            <RotateCcwIcon className="size-4" aria-hidden />
            Restaurar
          </Boton>
        )}
        {esRastro && !panel.dadaDeBaja && (
          <div className="flex shrink-0 items-center gap-1">
            <Boton
              variante="secundario"
              tamano="chico"
              onClick={() => {
                alEditar?.(panel)
              }}
            >
              <PencilIcon className="size-4" aria-hidden />
              Editar
            </Boton>
            <MenuDesplegable>
              <MenuDisparador asChild>
                <Boton variante="sutil" tamano="icono" aria-label="Más acciones">
                  <MoreVerticalIcon className="size-5" aria-hidden />
                </Boton>
              </MenuDisparador>
              <MenuContenido>
                {panel.estado !== 'borrador' && (
                  <MenuItem
                    onSelect={() => {
                      alEditarDatos?.(panel)
                    }}
                  >
                    <PencilIcon className="size-4" aria-hidden />
                    Editar datos
                  </MenuItem>
                )}
                <MenuItem
                  onSelect={() => {
                    alReprogramar?.(panel)
                  }}
                >
                  <CalendarIcon className="size-4" aria-hidden />
                  Reprogramar
                </MenuItem>
                <MenuItem
                  peligro
                  onSelect={() => {
                    alDarDeBaja?.(panel)
                  }}
                >
                  <Trash2Icon className="size-4" aria-hidden />
                  Dar de baja
                </MenuItem>
              </MenuContenido>
            </MenuDesplegable>
          </div>
        )}
      </DialogoEncabezado>

      {/* En escritorio cada columna se desplaza por su cuenta: la pieza queda
          quieta y entera, y los comentarios bajan en la suya. */}
      <DialogoCuerpo className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] lg:gap-8 lg:overflow-hidden">
        <div className="flex flex-col gap-3 lg:min-h-0 lg:overflow-y-auto">
          <VistaPieza
            usuarioInstagram={panel.cliente.usuarioInstagram}
            tipo={panel.tipo}
            copy={panel.copy}
            archivos={panel.archivos}
            alCambiarLamina={setLamina}
            {...(esRastro ? { alReemplazar: () => alEditar?.(panel) } : {})}
          />
          {panel.versiones.length > 1 && (
            <label className="flex items-center gap-2 text-sm">
              <span className="text-tinta-suave">Versión</span>
              <Selector
                aria-label="Versión"
                className="w-auto"
                value={String(panel.versionNumero)}
                onChange={(e) => {
                  const n = Number(e.target.value)
                  alCambiarVersion(n === panel.versionActual ? null : n)
                }}
              >
                {panel.versiones.map((v) => (
                  <option key={v.numero} value={v.numero}>
                    Versión {v.numero}
                    {v.numero === panel.versionActual ? ' · actual' : ''}
                  </option>
                ))}
              </Selector>
            </label>
          )}
        </div>

        <div className="mt-6 flex flex-col gap-4 lg:mt-0 lg:min-h-0 lg:overflow-y-auto">
          <div className="flex flex-wrap items-center gap-2">
            <ChipEstado estado={panel.estado} paraRastro={esRastro} />
            <span className="text-sm text-tinta-suave">
              Versión {panel.versionNumero}
            </span>
          </div>

          {mirandoAnterior && (
            <p
              role="status"
              className="flex flex-wrap items-center gap-2 rounded-2xl bg-lienzo px-4 py-3 text-sm"
            >
              Estás viendo la versión {panel.versionNumero}.
              <Boton
                variante="sutil"
                tamano="chico"
                onClick={() => {
                  alCambiarVersion(null)
                }}
              >
                Volver a la actual
              </Boton>
            </p>
          )}

          <dl className="space-y-1 text-sm">
            <div className="flex flex-wrap gap-x-2">
              <dt className="text-tinta-suave">Pilares:</dt>
              <dd>{panel.pilares.map((p) => p.nombre).join(', ') || '—'}</dd>
            </div>
            <div className="flex flex-wrap gap-x-2">
              <dt className="sr-only">Contexto</dt>
              <dd className="text-tinta-suave">{panel.contexto}</dd>
            </div>
          </dl>
          <p className="-mt-2 text-xs text-tinta-suave">
            Red: {panel.redes.map((r) => ETIQUETA_RED[r as RedSocial]).join(', ')}
          </p>

          {franja && (
            <p
              data-franja
              className={cn(
                'rounded-2xl px-4 py-3 text-sm',
                panel.estado === 'cambios'
                  ? 'bg-cambios text-cambios-tinta'
                  : 'bg-lienzo text-tinta',
              )}
            >
              {franja}
            </p>
          )}

          {/* El copy va una sola vez, debajo de la pieza, en la VistaPieza
              (revisión OO del 23/09/2026): acá le restaba lugar a los comentarios. */}

          <HiloComentarios
            comentarios={panel.comentarios}
            versionActual={panel.versionActual}
            esRastro={esRastro}
            laminaVisible={panel.tipo === 'carrusel' ? lamina : null}
            tipo={panel.tipo}
            borrador={borrador}
            alEscribir={alEscribir}
            alEnviar={enviarComentario}
            enviando={comentar.isPending}
            alMarcarResuelto={(comentarioId, resuelto) => {
              void resolver.mutateAsync({ comentarioId, resuelto })
            }}
            deshabilitado={mirandoAnterior}
          />

          {error && (
            <p
              role="alert"
              className="rounded-xl bg-nuevo/10 px-3.5 py-2.5 text-sm text-nuevo"
            >
              {error}
            </p>
          )}

          {enEscritorio &&
            decision('sticky bottom-0 mt-auto border-t border-borde bg-tarjeta pt-3')}
        </div>
      </DialogoCuerpo>

      {/* §8.4 — barra fija de decisión, sólo para el cliente. En escritorio va
          al pie de la columna derecha (arriba), así no le quita alto a la pieza. */}
      {!enEscritorio && decision('mt-auto border-t border-borde px-4 py-3 sm:px-6')}
    </>
  )
}
